/**
 * Cart API: get, update, add, merge (backend-only).
 * Uses apiClient for all HTTP.
 *
 * Functions accept an optional `ctx` parameter with values from AppContext.
 * If omitted, falls back to sessionStorage for backward compatibility.
 */
import { CART, CART_MERGE, CART_ACTIONS, CART_ADD } from '../../../shared/constants/api';
import * as api from '../../../shared/services/apiClient';
import { getGlobalStoreKey } from '../../../shared/services/apiClient';
import { getAnonymousId } from '../../../shared/utils/anonymousId';
import { CartView, CartAction } from '../types/cartTypes';
import { emitCartUpdate } from '../utils/cartEvents';

let isResolving = false;

const getScopedCartKey = () => {
  const storeKey = getGlobalStoreKey();
  return storeKey ? `cartId_${storeKey}` : 'cartId';
};

/** @param {object} [ctx] - Optional context override { cartId, currency, country } */
export const getCart = async (ctx?: { cartId?: string, currency?: string, country?: string }): Promise<CartView | null> => {
  const scopedKey = getScopedCartKey();
  const cartId = ctx?.cartId ?? sessionStorage.getItem(scopedKey) ?? localStorage.getItem(scopedKey);

  try {
  const params: Record<string, string> = cartId ? { cartId } : {};
    const res = await api.get(CART, params);

    if (res.status === 404) {
      sessionStorage.removeItem(scopedKey);
      localStorage.removeItem(scopedKey);
      return null;
    }

    if (!res.ok) {
      throw new Error(`Failed to fetch cart: ${res.status}`);
    }

    const cart = await res.json();

    const validInactiveStates = ['Ordered', 'Cancelled'];
    if (cart && cart.id && cart.cartState === 'Active') {
      if (scopedKey !== 'cartId') {
        sessionStorage.setItem(scopedKey, cart.id);
        localStorage.setItem(scopedKey, cart.id);
      }
    } else if (cart && validInactiveStates.includes(cart.cartState)) {
      // If the cart is already ordered or inactive, clear it and return null for a fresh start
      sessionStorage.removeItem(scopedKey);
      localStorage.removeItem(scopedKey);
      return null;
    }

    return cart;
  } catch (e) {
    console.error('[cartApi] Failed to fetch cart:', e);
    return null;
  }
};

export const resolveCartOnLogin = async (): Promise<CartView | null> => {
  if (isResolving) return null;
  isResolving = true;

  try {
    let customerEmail = null;
    const pingUserStr = sessionStorage.getItem('ping_user_info') || localStorage.getItem('ping_user_info');
    if (pingUserStr) {
      try {
        customerEmail = JSON.parse(pingUserStr).email;
      } catch (e) {
          console.warn('[cartApi] Failed to parse ping_user_info JSON:', e);
        }
    }
    if (!customerEmail) customerEmail = localStorage.getItem('ping_email');

    if (!customerEmail) {
      sessionStorage.setItem('cartResolved', 'true');
      return null;
    }

    // Find ALL store-scoped cart keys in localStorage (e.g. cartId_informa_market_us)
    const storeCartEntries = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('cartId_') || key === 'cartId')) {
        const storeKey = key === 'cartId' ? getGlobalStoreKey() || 'default' : key.replace('cartId_', '');
        const cartId = localStorage.getItem(key) || sessionStorage.getItem(key);
        storeCartEntries.push({ storeKey, cartId, localKey: key });
      }
    }

    // Always include the current store even if no cart exists yet (allows backend to find by email)
    const currentStoreKey = getGlobalStoreKey();
    if (currentStoreKey && !storeCartEntries.find(e => e.storeKey === currentStoreKey)) {
      storeCartEntries.push({ storeKey: currentStoreKey, cartId: null, localKey: `cartId_${currentStoreKey}` });
    }

    // If we have no entries at all (e.g. fresh incognito with no prior anonymous carts),
    // still attempt email-only merge for all well-known stores so previously
    // customer-linked carts from other sessions are recovered.
    const knownStores = ['informa_market_us', 'informa_market_uk']; // TODO: Consider fetching dynamically
    for (const storeKey of knownStores) {
      if (!storeCartEntries.find(e => e.storeKey === storeKey)) {
        storeCartEntries.push({ storeKey, cartId: null, localKey: `cartId_${storeKey}` });
      }
    }

    let lastMergedCart = null;

    // Run a merge for every known store
    for (const { storeKey, cartId, localKey } of storeCartEntries) {
      try {
        const anonymousId = getAnonymousId();
        const res = await api.post(CART_MERGE, {
          customerEmail,
          customerId: 'unknown',
          anonymousCartId: cartId || anonymousId,
        }, {
          headers: { 'x-store-key': storeKey }
        });

        if (res.ok) {
          const mergedCart = await res.json();
          if (mergedCart && mergedCart.id) {
            const finalKey = localKey === 'cartId' ? `cartId_${storeKey}` : localKey;
            sessionStorage.setItem(finalKey, mergedCart.id);
            localStorage.setItem(finalKey, mergedCart.id);
            // Clean up the legacy generic 'cartId' if we encountered it to prevent it hanging around
            if (localKey === 'cartId') {
              localStorage.removeItem('cartId');
              sessionStorage.removeItem('cartId');
            }
            if (storeKey === currentStoreKey) {
              lastMergedCart = mergedCart;
              emitCartUpdate(mergedCart);
            }
          }
        }
      } catch (e) {
        console.warn('[cartApi] Cart merge failed for store', storeKey, ':', e);
        // Continue with other stores even if one fails
      }
    }

    sessionStorage.setItem('cartResolved', 'true');
    return lastMergedCart;
  } catch (e) {
    console.error('[cartApi] resolveCartOnLogin failed:', e);
    sessionStorage.setItem('cartResolved', 'true');
    return null;
  } finally {
    isResolving = false;
  }
};

export const mergeAnonymousCart = async (): Promise<CartView | null> => {
  return await resolveCartOnLogin();
};

export const updateCart = async (actions: CartAction | CartAction[], existingCart: CartView | null = null): Promise<CartView | null> => {
  if (!Array.isArray(actions)) {
    actions = [actions];
  }

  const cart = existingCart || await getCart();
  if (!cart) return null;

  const isItemModification = actions.some((a: CartAction) =>
    ['addLineItem', 'removeLineItem', 'changeLineItemQuantity'].includes(a.action)
  );

  if (isItemModification && cart.discountCodes && cart.discountCodes.length > 0) {
    const removeDiscountActions: CartAction[] = cart.discountCodes
      .filter((dc: { discountCode?: { id: string } }) => dc.discountCode && dc.discountCode.id)
      .map((dc: { discountCode: { id: string } }) => ({
        action: 'removeDiscountCode' as const,
        discountCode: { typeId: 'discount-code' as const, id: dc.discountCode.id },
      }));
    actions = [...(Array.isArray(actions) ? actions : [actions]), ...removeDiscountActions];
  }

  try {
    const res = await api.post(CART_ACTIONS, {
      cartId: cart.id,
      version: cart.version,
      actions,
    });

    if (!res.ok) throw new Error(`Failed to update cart: ${res.status}`);

    const updatedCart = await res.json();
    emitCartUpdate(updatedCart);
    return updatedCart;
  } catch (e: unknown) {
    const error = e as { statusCode?: number; message?: string };
    if (error.statusCode === 409) {
      const latestCart = await getCart();
      if (!latestCart) return null;

      try {
        const retryRes = await api.post(CART_ACTIONS, {
          cartId: latestCart.id,
          version: latestCart.version,
          actions,
        });
        if (retryRes.ok) {
          const updatedCart = await retryRes.json();
          emitCartUpdate(updatedCart);
          return updatedCart;
        }
      } catch (retryErr) {
        console.error('[cartApi] Retry cart update failed:', retryErr);
        // Fall back to original error handling
      }
    }

    const isDiscountError = actions.some(
      (action: CartAction) =>
        action.action === 'addDiscountCode' || action.action === 'removeDiscountCode'
    );
    if (isDiscountError) {
      throw e;
    }
    return cart;
  }
};

/** @param {Array} items - Items to add @param {object} [ctx] - Optional { currency, country } */
export const addItemsToCart = async (
  items: Array<{ productId: string; variantId?: number | string; quantity?: number; custom?: Record<string, unknown>; externalPrice?: number; currency?: string }>, 
  ctx?: { cartId?: string, currency?: string, country?: string }
): Promise<CartView | null> => {
  if (!Array.isArray(items) || items.length === 0) {
    return null;
  }

  const scopedKey = getScopedCartKey();
  const cartId = ctx?.cartId ?? sessionStorage.getItem(scopedKey) ?? localStorage.getItem(scopedKey);
  const currency = ctx?.currency ?? sessionStorage.getItem('currency') ?? 'USD';
  const country = ctx?.country ?? sessionStorage.getItem('country');

  const formattedItems = items.map((item) => ({
    productId: item.productId,
    variantId: item.variantId,
    quantity: item.quantity || 1,
    custom: item.custom,
    externalPrice: item.externalPrice,
    currency: item.currency || currency,
  }));

  try {
    const res = await api.post(CART_ADD, {
      cartId,
      items: formattedItems,
      currency,
      country,
      anonymousId: getAnonymousId(),
    });

    if (!res.ok) throw new Error(`Failed to add items: ${res.status}`);

    const updatedCart = await res.json();
    if (updatedCart && updatedCart.id) {
      const scopedKey = getScopedCartKey();
      if (scopedKey !== 'cartId') {
        sessionStorage.setItem(scopedKey, updatedCart.id);
        localStorage.setItem(scopedKey, updatedCart.id);
      }
      emitCartUpdate(updatedCart);
    }
    return updatedCart;
  } catch (e: unknown) {
    const error = e as { statusCode?: number };
    if (cartId && (error.statusCode === 400 || error.statusCode === 404 || error.statusCode === 409)) {
      const activeCart = await getCart();
      if (!activeCart) {
        const scopedKey = getScopedCartKey();
        sessionStorage.removeItem(scopedKey);
        localStorage.removeItem(scopedKey);
        const newCtx = { ...ctx };
        delete newCtx.cartId;
        return await addItemsToCart(items, newCtx);
      }
    }
    console.error('[cartApi] addItemsToCart failed:', e);
    return null;
  }
};

export const addToCart = async (
  productId: string, 
  variantId?: string | number, 
  custom?: Record<string, unknown>, 
  quantity: number = 1, 
  externalPrice?: number
): Promise<CartView | null> => {
  return await addItemsToCart([
    {
      productId,
      variantId,
      custom,
      quantity,
      externalPrice,
    },
  ]);
};
